import React from 'react';
import { Trash2 } from 'lucide-react';
import { shouldShowDistinctMlValue, formatMacro } from '@/utils/gram-ml';
import { normalizeIngredientUnitKey } from '@/utils/ingredient-units';
import { formatIngredientDisplayName } from '@/lib/utils';

export interface Ingredient {
  id: string;
  /** Backend lookup id used for /ingredients/details and nutrition lookups (ObjectId, INxxxx, or spoonacular_xxx). */
  ingredientId?: string;
  /** Thumbnail from ingredient search when available. */
  imageUrl?: string;
  name: string;
  amount: number;
  unit: string;
  /** Ingredient amount resolved in grams for this specific recipe instance. */
  amountGrams?: number;
  /** Optional per-unit gram override (for example, 1 tub = 250g instead of 300g). */
  unitWeightGrams?: number;
  /** Optional per-unit ml override when known from grams↔ml mapping. */
  unitWeightMl?: number;
  /** Ingredient metadata used only for internal gram/ml conversions when returned by the API. */
  gramsPerMl?: number;
  yieldFactor?: number | null;
  retentionFactor?: number | null;
  /** Ingredient amount resolved in ml for this specific recipe instance when available. */
  amountMl?: number;
  /** Full nutrition from API (flat snake_case), kept in UI state for previews and totals. */
  nutrition?: Record<string, number>;
  /** Stored allergen labels/tags from the linked ingredient record. */
  allergens?: string[];
  /** Stored allergen tag IDs from the linked ingredient record. */
  allergenTagIds?: string[];
  /** Stored intolerance labels/tags from the linked ingredient record. */
  intoleranceTags?: string[];
  /** Stored intolerance tag IDs from the linked ingredient record. */
  intoleranceTagIds?: string[];
  preparationNote?: string | null;
  canEdit?: boolean;
  nutritionData?: {
    calories: number;
    macros: {
      protein: number;
      carbs: number;
      fat: number;
      fiber: number;
      saturatedFat: number;
      alcohol?: number;
    };
  };
}

interface IngredientListProps {
  ingredients: Ingredient[];
  unitLabelsByKey?: Record<string, string>;
  onEdit?: (ingredient: Ingredient) => void;
  onRemove: (id: string) => void;
}

const Dot = () => <span className="text-xs leading-[15px] text-[#d1d5dc]">·</span>;

function formatFactorPercent(value: unknown): string | null {
  if (value == null || value === '') return null;
  const numeric = Number(value);
  if (!Number.isFinite(numeric) || numeric <= 0) return null;
  const percent = numeric * 100;
  return `${Number.isInteger(percent) ? percent : Number(percent.toFixed(1))}%`;
}

export const IngredientList: React.FC<IngredientListProps> = ({
  ingredients,
  unitLabelsByKey = {},
  onEdit,
  onRemove,
}) => {
  if (ingredients.length === 0) {
    return (
      <div
        className="rounded-[10px] border border-dashed border-[#e2e8f0] bg-white px-4 py-10 text-center text-sm text-[#99a1af]"
        style={{ fontFamily: 'Inter, system-ui, sans-serif' }}
      >
        No ingredients added yet. Use &quot;Add Ingredients&quot; to start.
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-[30px]" style={{ fontFamily: 'Inter, system-ui, sans-serif' }}>
      {ingredients.map((ingredient) => {
        const displayName = formatIngredientDisplayName(ingredient.name);
        const amountRounded = formatMacro(ingredient.amount);
        const unitKey = ingredient.unit?.trim() ?? '';
        const normalizedKey = unitKey ? normalizeIngredientUnitKey(unitKey) : '';
        const unitLabel =
          (normalizedKey && unitLabelsByKey[normalizedKey])
          || (unitKey && unitLabelsByKey[unitKey])
          || ingredient.unit
          || '';
        const grams = Number(ingredient.amountGrams);
        const hasGrams = Number.isFinite(grams) && grams > 0;
        const ml = Number(ingredient.amountMl);
        const showMl = shouldShowDistinctMlValue(ingredient.amountGrams, ingredient.amountMl);
        const kcal = ingredient.nutritionData?.calories;
        const hasKcal = typeof kcal === 'number' && Number.isFinite(kcal);
        const retention = formatFactorPercent(ingredient.retentionFactor);
        const yieldValue = formatFactorPercent(ingredient.yieldFactor);

        return (
          <div
            key={ingredient.id}
            className="flex min-h-[47px] items-center justify-between pr-5"
          >
            <div className="flex min-w-0 max-w-[min(100%,381px)] items-center gap-2.5">
              <div className="h-12 w-12 min-w-[48px] shrink-0 overflow-hidden rounded-[14px] border border-[#e2e8f0] bg-white p-0.5 shadow-sm">
                {ingredient.imageUrl ? (
                  <img
                    src={ingredient.imageUrl}
                    alt=""
                    className="h-full w-full rounded-[12px] object-contain"
                  />
                ) : (
                  <div className="flex h-full w-full items-center justify-center rounded-[12px] bg-[#f3f4f6] text-sm font-semibold text-[#99a1af]">
                    {ingredient.name.trim().slice(0, 1).toUpperCase() || '?'}
                  </div>
                )}
              </div>
              <div className="flex min-w-0 flex-col gap-[5px]">
                <p className="truncate text-sm font-medium leading-[16.25px] text-[#1e293b]">
                  {displayName}
                </p>
                <div className="flex min-w-0 flex-wrap items-center gap-x-1 text-xs leading-[15.75px] tracking-[0.09px]">
                  <span className="inline-flex h-5 max-w-[140px] shrink-0 items-center truncate rounded-lg bg-[#f3f4f6] px-1.5 text-[#99a1af]">
                    {amountRounded} {unitLabel || 'portion'}
                  </span>
                  {hasGrams ? (
                    <>
                      <Dot />
                      <span className="whitespace-nowrap text-[#99a1af]">
                        {formatMacro(grams)} gm
                      </span>
                    </>
                  ) : null}
                  {showMl && Number.isFinite(ml) && ml > 0 ? (
                    <>
                      <Dot />
                      <span className="whitespace-nowrap text-[#99a1af]">
                        {formatMacro(ml)} ml
                      </span>
                    </>
                  ) : null}
                  {hasKcal ? (
                    <>
                      <Dot />
                      <span className="whitespace-nowrap font-medium text-[#fe9a00]">
                        {formatMacro(kcal)} kcal
                      </span>
                    </>
                  ) : null}
                  {retention ? (
                    <>
                      <Dot />
                      <span className="whitespace-nowrap text-[#99a1af]">
                        Ret {retention}
                      </span>
                    </>
                  ) : null}
                  {yieldValue ? (
                    <>
                      <Dot />
                      <span className="whitespace-nowrap text-[#99a1af]">
                        Yield {yieldValue}
                      </span>
                    </>
                  ) : null}
                </div>
              </div>
            </div>
            <div className="flex shrink-0 items-center gap-[23px] pl-2">
              {onEdit && ingredient.canEdit !== false ? (
                <button
                  type="button"
                  onClick={() => onEdit(ingredient)}
                  className="rounded-[10px] border border-[#cfd0d2] bg-white px-[17px] py-[5px] text-xs font-medium leading-4 text-[#0a0a0a] transition-colors hover:bg-slate-50"
                >
                  Edit
                </button>
              ) : null}
              <button
                type="button"
                onClick={() => onRemove(ingredient.id)}
                className="text-[#f87171] transition-colors hover:text-[#ef4444]"
                aria-label={`Remove ${ingredient.name}`}
              >
                <Trash2 className="h-6 w-6" strokeWidth={1.5} />
              </button>
            </div>
          </div>
        );
      })}
    </div>
  );
};
